import type { View, ViewProps } from "@-/com.npmjs.react-native";
import {
  measure,
  useAnimatedReaction,
  useAnimatedRef,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { LearnMapBottomClip } from "#learn-map-bottom-clip";
import { useLearnMapModeSelector } from "#use-learn-map-mode";

/** Clips more than any control row is tall, so an unmeasured row is hidden whole. */
const UNMEASURED_FULL_CLIP = 10_000;

/**
 * Controls at the bottom of Learn's cards (the deck's action row, the summary card's Start row)
 * that stay put while the card leaves for the map: the map's sheet slides in over them. They
 * draw above the map, so they are hidden below the sheet's top edge as it rises past them, and
 * uncovered as it goes back down. With no sheet to cover them (the map still loading), they are
 * gone once the map is open.
 */
export function LearnMapSheetCovered({
  children,
  contentClassName,
  ...props
}: ViewProps & { contentClassName?: string }) {
  const unmountedSheetTop = useSharedValue(Number.POSITIVE_INFINITY);
  const cardsProgress = useSharedValue(0);
  const sheetTop = useLearnMapModeSelector((mode) => mode.sheetTop) ?? unmountedSheetTop;
  const progress = useLearnMapModeSelector((mode) => mode.progress) ?? cardsProgress;
  const isMapPresent = useLearnMapModeSelector((mode) => mode.isMapPresent) ?? false;
  const ref = useAnimatedRef<View>();
  const clip = useSharedValue(0);
  useAnimatedReaction(
    () => {
      const top = sheetTop.get();
      const frame = measure(ref);
      if (frame === null) {
        // Not laid out yet. Learn opening on its map (a cold launch) mounts the controls under the
        // open map before their first layout: they start hidden, until a slide measures them.
        return progress.get() >= 1 ? UNMEASURED_FULL_CLIP : 0;
      }
      if (!Number.isFinite(top)) {
        return progress.get() >= 1 ? frame.height : 0;
      }
      return Math.min(frame.height, Math.max(0, frame.pageY + frame.height - top));
    },
    (next, previous) => {
      if (next !== previous) {
        clip.set(next);
      }
    },
  );
  return (
    <LearnMapBottomClip
      {...props}
      clip={clip}
      contentClassName={contentClassName}
      isClipping={isMapPresent}
      ref={ref}
    >
      {children}
    </LearnMapBottomClip>
  );
}
